import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND, NAV_LINKS } from "@/data/content";

const Navbar = ({ onContact }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    // Route path (e.g. "/portfolio")
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    // Anchor (e.g. "#services")
    if (location.pathname === "/") {
      const el = document.querySelector(href);
      if (el) {
        if (window.lenis) window.lenis.scrollTo(el, { offset: -80 });
        else el.scrollIntoView({ behavior: "smooth" });
      }
      window.history.replaceState(null, "", `/${href}`);
    } else {
      navigate(`/${href}`);
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A1428]/80 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-8 h-20 flex items-center justify-between">
        <button
          onClick={() => {
            if (location.pathname === "/") {
              if (window.lenis) window.lenis.scrollTo(0);
              else window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              navigate("/");
            }
          }}
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img src={BRAND.logo} alt={BRAND.name} className="h-11 w-11 rounded-lg object-contain" />
          <span className="hidden sm:block font-display font-bold text-lg tracking-tight text-white leading-none">
            TITAN<span className="text-[#1E6BFF]">.</span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-9">
          {NAV_LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="relative text-sm font-medium text-gray-300 hover:text-white transition-colors duration-200 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#1E6BFF] after:transition-all after:duration-300 hover:after:w-full"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onContact}
            data-testid="nav-plan-button"
            className="hidden sm:inline-flex items-center rounded-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white text-sm font-semibold px-6 py-2.5 transition-colors duration-200 glow-blue"
          >
            Get a Free Plan
          </button>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#0A1428]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="text-left text-lg font-medium text-gray-200 py-1"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  onContact();
                }}
                data-testid="nav-mobile-plan-button"
                className="mt-2 rounded-full bg-[#1E6BFF] text-white font-semibold px-6 py-3"
              >
                Get a Free Plan
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
